import { useEffect } from 'react'
import Echo from 'laravel-echo'
import Pusher from 'pusher-js'
import { useAuthStore } from '@/stores/authStore'

/**
 * Conexão de tempo real da Central de Atendimento (Laravel Reverb).
 *
 * Substitui o polling de 15s, que fazia dois atendentes responderem a mesma
 * pergunta sem se ver. Os canais são PRIVADOS: a autorização acontece no
 * servidor a cada inscrição, com o mesmo Bearer token da API.
 *
 * Sem Reverb configurado (`VITE_REVERB_APP_KEY` vazio), o hook não faz nada e
 * a tela segue com o polling de segurança — funciona, só não é instantâneo.
 */

type EchoInstance = InstanceType<typeof Echo>

let echo: EchoInstance | null = null

function client(): EchoInstance | null {
  const key = import.meta.env.VITE_REVERB_APP_KEY
  if (!key) return null
  if (echo) return echo

  // O Echo espera o Pusher no escopo global.
  ;(window as unknown as { Pusher: typeof Pusher }).Pusher = Pusher

  echo = new Echo({
    broadcaster: 'reverb',
    key,
    wsHost: import.meta.env.VITE_REVERB_HOST ?? window.location.hostname,
    wsPort: Number(import.meta.env.VITE_REVERB_PORT ?? 8080),
    wssPort: Number(import.meta.env.VITE_REVERB_PORT ?? 443),
    forceTLS: (import.meta.env.VITE_REVERB_SCHEME ?? 'http') === 'https',
    enabledTransports: ['ws', 'wss'],
    authEndpoint: `${import.meta.env.VITE_API_URL ?? '/api'}/broadcasting/auth`,
    auth: {
      headers: {
        // Mesma credencial da API: a autorização do canal roda no guard sanctum.
        Authorization: `Bearer ${useAuthStore.getState().accessToken ?? ''}`,
      },
    },
  })

  return echo
}

interface Handlers {
  onMessage?: (payload: unknown) => void
  onConversationUpdated?: (payload: unknown) => void
  /** Status de entrega mudou (entregue, lida, falhou). */
  onMessageStatus?: (payload: unknown) => void
}

/** Escuta uma conversa específica enquanto ela estiver aberta na tela. */
export function useSupportRealtime(conversationId: string | null, handlers: Handlers) {
  useEffect(() => {
    const instance = client()
    if (!instance || !conversationId) return

    const channel = instance.private(`support.conversation.${conversationId}`)
    channel.listen('.message.created', (e: unknown) => handlers.onMessage?.(e))
    channel.listen('.conversation.updated', (e: unknown) => handlers.onConversationUpdated?.(e))
    channel.listen('.message.status', (e: unknown) => handlers.onMessageStatus?.(e))

    return () => {
      instance.leave(`support.conversation.${conversationId}`)
    }
    // Os handlers vêm de closures que mudam a cada render; reinscrever a cada
    // troca derrubaria a conexão sem necessidade.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId])
}
